import { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  postStripeBillingPortalSessionUrlByAdvisorIdRequest,
  SubscriptionState,
} from '@client/model/subscriptionSeatsModel';
import { postStripeSubscriptionCheckoutSessionUrlRequest } from '@client/model/stripeSubscribeSessionModel';
import {
  selectSubscriptionSeatsState,
  selectStripeSubscribeSessionState,
  selectSession,
  selectSessionAdvisor,
} from '@client/model/selectors';
import { STRIPE_BILLING_URL } from '@client/util/stripe/urls';

interface UseSubscriptionActionsProps {
  subscription: SubscriptionState;
}

export function useSubscriptionActions({ subscription }: UseSubscriptionActionsProps) {
  const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { billingPortal } = useSelector(selectSubscriptionSeatsState);
  const stripeSubscribeSession = useSelector(selectStripeSubscribeSessionState);
  const { advisor } = useSelector(selectSession);
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const priceId = undefined;

  // Handle billing portal redirect or fallback to static URL on error
  useEffect(() => {
    if (billingPortal.status === 'success' && billingPortal.url) {
      window.location.href = billingPortal.url;
    } else if (billingPortal.status === 'error') {
      // Fallback to static billing URL if dynamic portal fails
      window.open(STRIPE_BILLING_URL, '_blank', 'noopener,noreferrer');
    }
  }, [billingPortal.status, billingPortal.url]);

  // Handle subscription checkout redirect
  useEffect(() => {
    const { isLoading, success, url } = stripeSubscribeSession;

    if (!isLoading && success && url) {
      window.location.href = url;
    }
  }, [stripeSubscribeSession]);

  // Handle manage billing button click
  const handleManageBilling = useCallback(() => {
    if (sessionAdvisor?.advisorId && subscription?.stripeSubscriptionId) {
      dispatch(
        postStripeBillingPortalSessionUrlByAdvisorIdRequest({
          advisorId: sessionAdvisor.advisorId,
          stripeSubscriptionId: subscription.stripeSubscriptionId,
        }),
      );
    }
  }, [dispatch, sessionAdvisor?.advisorId, subscription?.stripeSubscriptionId]);

  // Handle create new subscription
  const handleCreateSubscription = useCallback(() => {
    dispatch(
      postStripeSubscriptionCheckoutSessionUrlRequest({
        advisorId: advisor?.advisorId ?? '',
        priceId,
      }),
    );
  }, [advisor?.advisorId, dispatch, priceId]);

  const isLoadingBillingPortal = billingPortal.status === 'loading';
  const isLoadingCheckout = stripeSubscribeSession.isLoading;

  // Handle cancellation modal
  const handleOpenCancellationModal = useCallback(() => {
    setIsCancellationModalOpen(true);
  }, []);

  const handleCloseCancellationModal = useCallback(() => {
    setIsCancellationModalOpen(false);
  }, []);

  return {
    handleManageBilling,
    handleCreateSubscription,
    isLoadingBillingPortal,
    isLoadingCheckout,
    isCancellationModalOpen,
    handleOpenCancellationModal,
    handleCloseCancellationModal,
  };
}
